import { Suspense, useState } from "react";
import { Outlet } from "react-router-dom";

import { useApp } from "@/context/app-provider";
import { PageNavSlotProvider } from "@/context/PageNavSlotProvider";

import { Loading } from "@/components/module/Loading";
import { OrgSwitcher } from "@/components/module/OrgSwitcher";
import { Sidebar } from "@/components/module/Sidebar";

export const Layout: React.FC = () => {
  const { topbarName } = useApp();
  const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);

  /**
   * The shell is viewport-height and `main` does the scrolling, rather than the
   * document. The sidebar is already `fixed`, so it never scrolled with the
   * document anyway — and a page-level `sticky` element only works when its
   * nearest scroll container is the thing that actually scrolls.
   *
   * `min-h-0` on both flex children is load-bearing: a flex item defaults to
   * `min-height: auto`, which refuses to shrink below its content, so `main`
   * would grow past the viewport and never scroll internally.
   */
  return (
    <div className="app-root flex h-screen w-full">
      <Sidebar />
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="border-border bg-card flex h-14 items-center justify-between border-b px-6">
          <span className="text-text text-xl font-medium">{topbarName}</span>
          <OrgSwitcher />
        </header>
        <div className="flex min-h-0 flex-1">
          {/**
           * Empty until a page portals its nav rail in, and zero-width while
           * it is — the rail carries its own width and border, so a page with
           * no nav costs nothing here. It sits beside `main` rather than
           * inside it, which is what keeps it still while the page scrolls.
           */}
          <div ref={setNavSlot} className="flex shrink-0" />
          <main
            className="bg-background min-h-0 flex-1 scrollbar-gutter-stable overflow-x-hidden overflow-y-auto"
            style={{ viewTransitionName: "main-content" }}
          >
            <PageNavSlotProvider slot={navSlot}>
              <Suspense fallback={<Loading />}>
                <Outlet />
              </Suspense>
            </PageNavSlotProvider>
          </main>
        </div>
      </div>
    </div>
  );
};
